<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  // @ts-ignore
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
  // @ts-ignore
  import * as AlertDialog from '$lib/components/ui/alert-dialog';
  // @ts-ignore
  import { Button } from '$lib/components/ui/button';
  import Icon from '$shared/icons/Icon.svelte';
  import QuestionSetTextField from './QuestionSetTextField.svelte';
  import { sortableList } from './sortableList';
  import type { QuestionSetQuestion } from './curationTypes';

  export let question: QuestionSetQuestion;
  export let url: string;
  export let isFirst = false;
  export let isLast = false;

  export let multipleChoiceText = 'Multiple choice';
  export let openEndedText = 'Open-ended';
  export let questionPlaceholder = 'Enter your question here...';
  export let responsePreviewText = 'Learners will type their response here.';
  export let moveLabel = 'Move question';
  export let moveTopLabel = 'Move to top';
  export let moveUpLabel = 'Move up';
  export let moveDownLabel = 'Move down';
  export let moveBottomLabel = 'Move to bottom';
  export let duplicateLabel = 'Duplicate question';
  export let deleteLabel = 'Delete question';
  export let optionPlaceholder = 'Enter option here...';
  export let addOptionText = 'Add option';
  export let deleteOptionLabel = 'Delete option';
  export let deleteOptionConfirmText = 'Are you sure you want to delete this option?';
  export let deleteText = 'Delete';
  export let cancelText = 'Cancel';

  const dispatch = createEventDispatcher();

  $: isMultipleChoice = question.question_type === 'multiple_choice';

  let options = question.options;
  let addingOption = false;

  // Adds an empty option at the end of the question. Clicks while one is being added are ignored.
  async function addOption() {
    if (addingOption) return;
    addingOption = true;

    try {
      const response = await fetch(`${url}/options`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      });
      if (!response.ok) return;

      const data = await response.json();
      options = [...options, data.option];
    } finally {
      addingOption = false;
    }
  }

  let deleteDialogOpen = false;
  let optionToDelete: number;

  // Opens the "Are you sure?" dialog for an option. Only offered when there are more than two.
  function askToDeleteOption(optionId: number) {
    optionToDelete = optionId;
    deleteDialogOpen = true;
  }

  // Deletes the option the owner confirmed in the dialog.
  async function deleteOption() {
    const optionId = optionToDelete;
    const response = await fetch(`${url}/options/${optionId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    });
    if (response.ok) options = options.filter((option) => option.id !== optionId);
  }

  // Moves an option from one place to another after a drag, then saves its new place (1 is the top).
  function moveOptionTo(from: number, to: number) {
    const reordered = [...options];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    options = reordered;

    fetch(`${url}/options/${moved.id}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ option: { position: to + 1 } }),
    });
  }
</script>

<!-- Duplicate and delete are shown but don't do anything yet. -->
<div class="flex items-start gap-2">
  <div class="question-handle w-6 pt-1 cursor-grab" aria-hidden="true">
    <Icon icon="GripVertical" size="medium" />
  </div>

  <div class="flex flex-1 min-w-0 flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-6">
    <div class="flex items-start justify-between gap-4">
      <span class="flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2 text-base font-medium text-neutral-600">
        <Icon icon={isMultipleChoice ? 'List' : 'MessageSquare'} size="small" />
        {isMultipleChoice ? multipleChoiceText : openEndedText}
      </span>

      <div class="flex items-center shrink-0">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild let:builder>
            <Button builders={[builder]} variant="ghost" size="icon" class="w-8 h-8 p-0">
              <span class="sr-only">{moveLabel}</span>
              <Icon icon="ArrowUpDown" size="small" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            <DropdownMenu.Item class="cursor-pointer" disabled={isFirst} on:click={() => dispatch('move', 'top')}>
              <Icon icon="ChevronsUp" size="small" iconClass="mr-1" />{moveTopLabel}
            </DropdownMenu.Item>
            <DropdownMenu.Item class="cursor-pointer" disabled={isFirst} on:click={() => dispatch('move', 'up')}>
              <Icon icon="ChevronUp" size="small" iconClass="mr-1" />{moveUpLabel}
            </DropdownMenu.Item>
            <DropdownMenu.Item class="cursor-pointer" disabled={isLast} on:click={() => dispatch('move', 'down')}>
              <Icon icon="ChevronDown" size="small" iconClass="mr-1" />{moveDownLabel}
            </DropdownMenu.Item>
            <DropdownMenu.Item class="cursor-pointer" disabled={isLast} on:click={() => dispatch('move', 'bottom')}>
              <Icon icon="ChevronsDown" size="small" iconClass="mr-1" />{moveBottomLabel}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        <Button variant="ghost" size="icon" class="w-8 h-8 p-0">
          <span class="sr-only">{duplicateLabel}</span>
          <Icon icon="Copy" size="small" />
        </Button>
        <Button variant="ghost" size="icon" class="w-8 h-8 p-0">
          <span class="sr-only">{deleteLabel}</span>
          <Icon icon="Trash2" size="small" />
        </Button>
      </div>
    </div>

    <QuestionSetTextField {url} model="question" value={question.content} placeholder={questionPlaceholder} multiline maxlength={2000} />

    {#if isMultipleChoice}
      <div class="flex flex-col gap-2 pl-6">
        <div class="flex flex-col gap-2" use:sortableList={{ handle: '.option-handle', onMove: moveOptionTo }}>
          {#each options as option (option.id)}
            <div class="flex items-start gap-2">
              <div class="option-handle w-6 pt-2.5 cursor-grab" aria-hidden="true">
                <Icon icon="GripVertical" size="medium" />
              </div>
              <QuestionSetTextField
                url={`${url}/options/${option.id}`}
                model="option"
                value={option.content}
                placeholder={optionPlaceholder}
                maxlength={255}
              />
              <!-- Hidden at two options, since a multiple choice question needs at least two. -->
              {#if options.length > 2}
                <Button variant="ghost" size="icon" class="w-8 h-8 p-0 mt-1.5" on:click={() => askToDeleteOption(option.id)}>
                  <span class="sr-only">{deleteOptionLabel}</span>
                  <Icon icon="Trash2" size="small" />
                </Button>
              {/if}
            </div>
          {/each}
        </div>
        <div>
          <Button variant="ghost" class="h-10 gap-2 px-4 font-medium" on:click={addOption}>
            <Icon icon="Plus" size="small" />
            {addOptionText}
          </Button>
        </div>
        <AlertDialog.Root bind:open={deleteDialogOpen}>
          <AlertDialog.Content>
            <AlertDialog.Header>
              <AlertDialog.Title>{deleteOptionConfirmText}</AlertDialog.Title>
            </AlertDialog.Header>
            <AlertDialog.Footer>
              <AlertDialog.Cancel>{cancelText}</AlertDialog.Cancel>
              <AlertDialog.Action on:click={deleteOption}>{deleteText}</AlertDialog.Action>
            </AlertDialog.Footer>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </div>
    {:else}
      <div class="h-[84px] rounded-lg bg-neutral-100 p-3 text-base leading-[26px] text-neutral-400" aria-hidden="true">
        {responsePreviewText}
      </div>
    {/if}
  </div>
</div>
